import {
  createColumnHelper, SortingState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, { useCallback,useMemo, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ModalMetricCreate from '@/app/evaluations/_modalMetricCreate/modalMetricCreate';
import { Button } from '@/components/button/button';
import { DeprecationToggle } from '@/components/deprecationToggle/deprecationToggle';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { useCreateMetricMutation, useMetricProviders } from '@/hooks/queries/useMetrics';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { MetricConfig, MetricProviderCreateReq } from '@/types/evals';
import {
  dateFilterToISO, OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum
} from '@/utils/timeFormatter';

import styles from './page.module.scss';

const columnHelper = createColumnHelper<MetricConfig>();
const SERVER_SORT_COLUMNS: Record<string, string> = { name: 'name', created_at: 'created_at' };

export default function EvaluationsMetricPage() {
  const navigate = useNavigate();
  const [ showModalCreate, setShowModalCreate ] = useState<boolean>();
  const [ useLocalZone ] = useLocalZonePreference();
  const [ showDeprecated, setShowDeprecated ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [ { id: 'created_at', desc: true } ] );
  const [ pageSize, setPageSize ] = useState( 30 );
  const { cursors, currentCursorIdx, currentCursor, currentPage, canGoPrev, goNext, goPrev, reset: resetCursor } = useCursorPagination();
  const [ nameFilter, setNameFilter ] = useState( '' );
  const [ startDate, setStartDate ] = useState( '' );
  const [ endDate, setEndDate ] = useState( '' );
  const [ committedFilters, setCommittedFilters ] = useState( {
    name: '',
    startDate: '',
    endDate: ''
  } );

  const activeSort = sorting[0] as SortingState[0] | undefined;

  const { data, isLoading: isLoadingMetrics, isError } = useMetricProviders( {
    limit: pageSize,
    cursor: currentCursor ?? undefined,
    name: committedFilters.name || undefined,
    is_deprecated: showDeprecated ? undefined : false,
    created_at_from: committedFilters.startDate ? dateFilterToISO( committedFilters.startDate, useLocalZone ) : undefined,
    created_at_to: committedFilters.endDate ? dateFilterToISO( committedFilters.endDate, useLocalZone, true ) : undefined,
    sort_by: currentCursor ? undefined : ( activeSort ? SERVER_SORT_COLUMNS[ activeSort.id ] : undefined ),
    sort_order: currentCursor ? undefined : ( activeSort ? ( activeSort.desc ? 'desc' : 'asc' ) : undefined )
  } );

  const pageItems = data?.items ?? [];
  const createMetricMutation = useCreateMetricMutation();

  /* COLUMNS */
  const columns = useMemo( () => [
    columnHelper.accessor( ( row ) => row.name, {
      id: 'name',
      header: 'Name',
      size: 300,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-name-${ metric.metric_provider_id }` }>
            {info.getValue()}
            { metric.is_deprecated && (
              <span className='deprecated_tag'>(deprecated)</span>
            ) }
          </span>
        );
      },
      enableSorting: true,
      meta: {
        classStr: buildColumnClassString( { width: 30, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( ( row ) => row.description, {
      id: 'description',
      header: 'Description',
      size: 250,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-description-${ metric.metric_provider_id }` }>
            {info.getValue() || '-'}
          </span>
        );
      },
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor(
      ( row ) => row.created_at,
      {
        id: 'createdAt',
        header: 'Created',
        size: 250,
        cell: info => {
          const value = info.getValue();
          return value ? (
            <TimeFormatter
              sourceType={ TimeSourceTypeEnum.ISO }
              value={ value }
              outputFormat={ OutputFormatEnum.STRING_SINGLE }
              opts={ { useLocalZone } }
            />
          ) : (
            <span>-</span>
          );
        },
        enableSorting: true,
        meta: {
          classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } )
        }
      } ),
    columnHelper.accessor( ( row ) => row.latest_version_number, {
      id: 'latestVersion',
      header: '# of Versions',
      size: 150,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-version-${ metric.metric_provider_id }` }>
            {info.getValue()}
          </span>
        );
      },
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( ( row ) => row.version, {
      id: 'latestActiveVersion',
      header: 'Latest Active Version',
      size: 150,
      cell: info => {
        const metric = info.row.original;
        return (
          <span data-testid={ `metric-active-version-${ metric.metric_provider_id }` }>
            {/* version is number|undefined — ?? (not ||) so a real version 0 renders as 0 instead of '-' */}
            {info.getValue() ?? '-'}
          </span>
        );
      },
      enableSorting: false,
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } )
  ], [ useLocalZone ] );

  const handleRowClick = useCallback( ( metric: MetricConfig ) => {
    const params = new URLSearchParams( {
      metric_provider_id: metric.metric_provider_id,
      version: String( metric.version ?? metric.latest_version_number )
    } );
    navigate( `/evaluations/metric?${ params.toString() }` );
  }, [ navigate ] );

  const handleNextPage = useCallback( () => {
    if ( data?.pagination.next_cursor ) goNext( data.pagination.next_cursor );
  }, [ data?.pagination.next_cursor, goNext ] );

  const tableState = useMemo( () => ( { sorting } ), [ sorting ] );

  const table = useNativeTable<MetricConfig>( {
    data: pageItems,
    columns,
    state: tableState,
    onSortingChange: ( updater ) => {
      setSorting( updater );
      resetCursor();
    },
    manualPagination: true,
    manualSorting: true
  } );

  const handleApplyFilters = useCallback( () => {
    setCommittedFilters( { name: nameFilter, startDate, endDate } );
    resetCursor();
  }, [ nameFilter, startDate, endDate, resetCursor ] );

  const handleClearFilters = useCallback( () => {
    setNameFilter( '' );
    setStartDate( '' );
    setEndDate( '' );
    setCommittedFilters( { name: '', startDate: '', endDate: '' } );
    resetCursor();
  }, [ resetCursor ] );

  /* HANDLERS */
  const onMetricCreate = ( args: MetricProviderCreateReq ) => {
    createMetricMutation.mutate(
      args,
      {
        onSuccess: () => {
          toast.success( 'Metric created successfully' );
          setShowModalCreate( false );
        },
        onError: ( error ) => {
          console.error( 'Failed to create metric:', error );
          toast.error( error instanceof Error ? error.message : 'Failed to create metric' );
        }
      }
    );
  };

  const isLoading = isLoadingMetrics;
  const isPending = createMetricMutation.isPending;

  return (
    <div className="page" data-testid="metrics-page">
      <ToastContainer />
      <>
        {showModalCreate && (
          <ModalMetricCreate
            cb={ onMetricCreate }
            onClose={ () => {
              if ( !isPending ) {
                setShowModalCreate( false );
              }
            } }
            isSubmitting={ isPending }
          />
        )}

        <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
          <span className='info_name'>
            <Heading as="h2" data-testid="metrics-heading">Metrics</Heading>
          </span>
          <span className='info_actions'>
            <DeprecationToggle
              entityNamePlural='metrics'
              showDeprecated={ showDeprecated }
              onChange={ ( next ) => {
                setShowDeprecated( next );
                resetCursor();
              } }
            />
            <Button
              variant='primary'
              icon={ <Icon icon={ FiPlus } /> }
              data-testid="create-metric-button"
              onClick={ () => setShowModalCreate( !showModalCreate ) }
            >
              Create Metric
            </Button>
          </span>
        </div>

        {!isLoading && !isError && (
          <div className={ clsx( 'layout__row', 'pad', 'filters' ) }>
            <div className='filter_field'>
              <Input
                id="name-filter"
                type='text'
                label='Filter by name'
                placeholder='Filter by name...'
                value={ nameFilter }
                onChange={ ( e ) => setNameFilter( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
              />
            </div>
            <div className='filter_field'>
              <label htmlFor="start-date-filter" className='filter_label'>Start date</label>
              <input
                id="start-date-filter"
                type='date'
                value={ startDate }
                onChange={ ( e ) => setStartDate( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
                className='filter_input'
                title="Filter by start date"
              />
            </div>
            <div className='filter_field'>
              <label htmlFor="end-date-filter" className='filter_label'>End date</label>
              <input
                id="end-date-filter"
                type='date'
                value={ endDate }
                onChange={ ( e ) => setEndDate( e.target.value ) }
                onKeyDown={ ( e ) => {
                  if ( e.key === 'Enter' ) {
                    handleApplyFilters();
                  }
                } }
                className='filter_input'
                title="Filter by end date"
              />
            </div>
            <Button
              variant='secondary'
              onClick={ handleClearFilters }
              title="Clear all filters"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
            <Button
              variant='primary'
              onClick={ handleApplyFilters }
              title="Apply filters"
              data-testid="apply-filters-button"
            >
              Apply Filters
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className={ clsx( 'layout__row', styles.layout__evals ) }>
            <Loader />
          </div>
        ) : (
          <div data-testid="metrics-table-container">
            <NativeTable
              table={ table }
              tbodyTestId="table-body-metrics"
              onRowClick={ handleRowClick }
              cursorPagination={ {
                hasMore: data?.pagination.has_more ?? false,
                canGoPrev,
                currentPage,
                pageSize,
                onNextPage: handleNextPage,
                onPrevPage: goPrev,
                onPageSizeChange: ( size ) => { setPageSize( size ); resetCursor(); }
              } }
            />
          </div>
        )}
        {isPending && (
          <div data-testid="loading-overlay">
            <Loader />
          </div>
        )}
      </>
    </div>
  );
}
